/**
 * «Данные УНТИ» API — contract #36/#37 (ADDITIVE v1.9.38, T.U1) — the read
 * surface T.U2's page is the only consumer of.
 *
 *   #36 GET /api/unti/applications     → `{ data: { items: [...] }, meta: { page, pageSize, total } }`
 *   #37 GET /api/unti/applications/:id → `{ data: UntiApplicationDto & { changes: [...] } }`
 *
 * ADMIN floor on both (`x-contract-role: ADMIN`) — the mirror carries `pgu_id`,
 * person-identifying. CACHE-ONLY: nothing here talks to cat.2035.university;
 * each row's `lastSeenAt` is the honest staleness marker.
 *
 * The shapes are mirrored 1:1 from the generated
 * `packages/shared/src/openapi/api-types.ts` (`UntiApplicationFieldValues` +
 * `UntiApplicationDto`/`UntiApplicationChangeDto` and the two envelopes), the
 * same rule `applications.ts` documents: the generated file is not reachable
 * from here (`@cb-manager/shared` exports only its barrel), so the mirror lives
 * in one place — this file. The wire truth is `apps/api/src/unti/dto.ts`.
 *
 * Every mirror field arrives VERBATIM TEXT (the worker stored the API's own
 * rendering — `unti_applications` is all TEXT by design): `status`,
 * `transfert_status`, `cof_exit_exam_status` are THEIR vocabulary, never an
 * enum of ours, and their dates stay the DRF strings the wire carried. The api
 * adds only `id` (the surrogate PK — the #37 path parameter) and the two
 * freshness clocks (`firstSeenAt`/`lastSeenAt`).
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { buildQuery, requestEnvelope, type EnvelopeMeta } from './client';

// ---------------------------------------------------------------------------

/** One row of #36 — the 39 API fields verbatim, plus the surrogate and the clocks. */
export interface UntiApplicationDto {
  /** Surrogate PK of the mirror row — the #37 path parameter. */
  id: number;
  /** Their API `id` (TEXT, verbatim; the sweep's upsert key). */
  applicationId: string;
  /** The УНТИ leaf id — the join key to `applications.unti_id`. */
  untiId: string;
  /** Their course id TEXT (the same id space as our `programs` dictionary). */
  courseId: string | null;
  /** Person-identifying, stored verbatim — the ADMIN floor exists for it. */
  pguId: string | null;
  pguDt: string | null;
  serviceOkato: string | null;
  /** RAW as their API spells it (`approved`/`accepted`/`declined` seen live). */
  status: string;
  /** RAW — their undocumented vocabulary (`booked`/`activated`; the typo is theirs). */
  transfertStatus: string | null;
  /** Their DRF datetime string; the list read orders by its timestamptz cast. */
  appliedAt: string | null;
  flowId: string | null;
  flowNum: string | null;
  futureCodeModuleId: string | null;
  futureCodeModuleNum: string | null;
  difficultyLevel: string | null;
  admissionOrderType: string | null;
  admissionOrderNumber: string | null;
  admissionOrderDate: string | null;
  expulsionDate: string | null;
  expulsionOrderType: string | null;
  expulsionOrderNumber: string | null;
  expulsionOrderDate: string | null;
  educationEndDate: string | null;
  endDt: string | null;
  /** THEIR «дата автоотклонения» (their own instant, no arithmetic of ours; feeds our «Дедлайн договора» since T.Z187/v1.9.59). */
  untillDt: string | null;
  algorithmGrade: string | null;
  languageGrade: string | null;
  motivationGrade: string | null;
  isTimeout: string | null;
  /** Their exit-exam state (`start|InProcess|Success|UnSuccess|NoStartExpired`; `''` seen live). */
  cofExitExamStatus: string | null;
  exitAlgorithmGrade: string | null;
  exitLanguageGrade: string | null;
  exitMotivationGrade: string | null;
  exitEndDt: string | null;
  finalExamDate: string | null;
  finalExamOrderDate: string | null;
  finalExamOrderNumber: string | null;
  certificateUrl: string | null;
  certificateNumber: string | null;
  /** Always null live so far (their «СМОТРЕТЬ НА FTP» pointer). */
  additionalInfo: string | null;
  /** First sweep that saw the application. */
  firstSeenAt: string;
  /** Last sweep that saw it — the staleness marker the page renders. */
  lastSeenAt: string;
  /**
   * ADDITIVE (v1.9.40, T.Z147) — OUR application by the UNIQUE `unti_id`, or
   * `null` when no application of ours carries this id. OPTIONAL on the type
   * (an older api / a stub without the block reads «нет наших данных», never a
   * crash); the api always serialises the key.
   */
  our?: UntiApplicationOurDataDto | null;
}

/** One `unti_application_changes` row — what a sweep CHANGED on a mirror row. */
export interface UntiApplicationChangeDto {
  id: number;
  /** The API field name, verbatim (`status`, `transfert_status`, …). */
  field: string;
  /** The verbatim value before (`null` = the field was empty). */
  oldValue: string | null;
  /** The verbatim value after. */
  newValue: string | null;
  /** When the sweep that saw the change ran. */
  seenAt: string;
  /** The sweep's opaque token (`unti2035-<ISO>`), grouping one pass's rows. */
  sweepId: string;
}

/** #37 — the row plus its full change history (oldest last). */
export interface UntiApplicationDetailDto extends UntiApplicationDto {
  changes: UntiApplicationChangeDto[];
}

/**
 * ADDITIVE (v1.9.40, T.Z147) — OUR half of one mirror row: the application of
 * ours the row's unti_id belongs to (the UNIQUE `applications.unti_id` join —
 * never pgu_id/course), everything the «Наш статус»/«Формат»/«Договор»/
 * «Провалил ЕВИ» columns and the child card render. Read-only: the join writes
 * nothing. `null` = no application of ours carries this unti_id.
 */
export interface UntiApplicationOurDataDto {
  /** Our `applications.id` — where the register link lands. */
  applicationId: number;
  /** The STORED funnel status (the engine's verdict; the display fold rides `eviFailed`). */
  ourStatus: 'WAITING_LINK' | 'LINK_RECEIVED' | 'EVI_PASSED' | 'REJECTED' | 'AWAITING_INFO' | 'NO_DATA';
  /** The «Провалил ЕВИ» fold (`evi_failed OR evi_failed_manually`, either source). */
  eviFailed: boolean;
  format: 'OFFLINE' | 'ONLINE' | null;
  contractArrived: boolean;
  contractSigned: boolean;
  contractNotArrived: boolean;
  /** The register's own fold — `null` = the sync has no fact for this row yet. */
  contractState: 'SIGNED' | 'NOT_SIGNED' | 'NOT_ARRIVED' | null;
}

export interface UntiApplicationListDto {
  items: UntiApplicationDto[];
}
/**
 * The contract's `{data, meta}` page envelope (`#36`). ADDITIVE (v1.9.40,
 * T.Z147) — `meta` grew the OPTIONAL `notFoundInOurData` (the shared `Envelope`
 * type stays untouched for every other list, the `UntiPaginationMeta` wire).
 */
export interface UntiApplicationListEnvelope {
  data: UntiApplicationListDto;
  meta?: EnvelopeMeta & { notFoundInOurData?: number };
}
export interface UntiApplicationDetailEnvelope {
  data: UntiApplicationDetailDto;
}

/**
 * The #36 query. EVERY wire value arrives a STRING (the service coerces with
 * the digits-only rule, the same lenient reading `common/pagination.ts` gives
 * `page`/`pageSize`); the web side keeps numbers for ids and lets `buildQuery`
 * stringify them.
 *
 * ADDITIVE (v1.9.40, T.Z147) — the five our-half filters over the JOINED
 * application: `ourStatus` (#3's exact status grammar, the six frozen values
 * plus the VIRTUAL `EVI_FAILED`), `format`, `contract` (`signed|not_signed|
 * not_arrived` — the register's own literals), `eviFailed` (the fold, not the
 * auto column) and `linked` (the join state; `false` = «не найдено в наших
 * данных»). The model drops anything the api would 400 before it is sent.
 */
export interface UntiApplicationListQuery {
  page?: number;
  pageSize?: number;
  /** Their course id (= our programme id, one id space). */
  courseId?: number;
  /** RAW status equality — their vocabulary, sent verbatim. */
  status?: string;
  /** Exact unti_id. */
  untiId?: string;
  /** Substring over unti_id / pgu_id (the mirror has no other searchable field). */
  search?: string;
  /**
   * ADDITIVE (v1.9.88, T.Z238) — the «Трансферт» word of the RAW mirror row's
   * OWN `transfert_status`: ONE value verbatim (their vocabulary, never
   * translated — the cell prints the same string) or the api's own `none`
   * literal for the NULL/'' bucket (the «Не указан» rows, the same token the
   * register's `?transfert=` speaks). Matched against the ROW's column — NOT
   * the register's leaf-winner fold — and anything the mirror does not carry
   * is the api's 400; the option list comes from the #36.1 facet below, so the
   * select can never offer a value the filter refuses.
   */
  transfert?: string;
  /**
   * ADDITIVE (v1.9.42, T.Z153) — the «Итоговое испытание» status of the MIRROR
   * column `cof_exit_exam_status`: one of the five raw literals
   * (`start|InProcess|Success|UnSuccess|NoStartExpired`) or a comma list of
   * them, sent verbatim; anything else is the api's 400, so the model drops it
   * before the request.
   */
  exitExamStatus?: string;
  /** OUR display status — one value or a comma list (`EVI_FAILED,EVI_PASSED`). */
  ourStatus?: string;
  /** OUR «Формат обучения». */
  format?: string;
  /** The contract-fact bucket of the joined application. */
  contract?: string;
  /** The «Провалил ЕВИ» fold of the joined application. */
  eviFailed?: boolean;
  /** `true` = only rows our data has; `false` = only the «не найдено» rows. */
  linked?: boolean;
  /**
   * ADDITIVE (v1.9.43, T.Z154) — the SERVER-side order over the WHOLE filtered
   * set: one of #36's twelve whitelisted column keys. Absent = the api's
   * standing `applied_at`-DESC ordering (the default view sends neither key).
   */
  sort?: string;
  /** The direction of `sort` (`asc` default); sent only together with it. */
  dir?: 'asc' | 'desc';
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

export function untiApplicationsPath(query: UntiApplicationListQuery = {}): string {
  return `/unti/applications${buildQuery(query)}`;
}

export function untiApplicationDetailPath(id: number): string {
  return `/unti/applications/${encodeURIComponent(id)}`;
}

/** #36.1 (ADDITIVE v1.9.88, T.Z238) — the «Трансферт» facet of #36. */
export const UNTI_TRANSFERT_VALUES_PATH = '/unti/applications/transfert-values';

export async function fetchUntiApplications(
  query: UntiApplicationListQuery,
  signal?: AbortSignal,
): Promise<UntiApplicationListEnvelope> {
  return requestEnvelope<UntiApplicationListDto>('GET', untiApplicationsPath(query), ['data'], { signal });
}

export async function fetchUntiApplicationDetail(
  id: number,
  signal?: AbortSignal,
): Promise<UntiApplicationDetailEnvelope> {
  return requestEnvelope<UntiApplicationDetailDto>('GET', untiApplicationDetailPath(id), ['data'], { signal });
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Cache identity of one list slice — every filter and the page are part of it. */
export function untiApplicationsQueryKey(query: UntiApplicationListQuery): readonly ['unti', 'list', UntiApplicationListQuery] {
  return ['unti', 'list', query] as const;
}

/**
 * #36 — one page of the mirror. `keepPreviousData` keeps the table mounted
 * while a filter change is in flight (the register's rule), and the page polls
 * like every live read here — the worker's hourly sweep is the writer, so a
 * slower tick would be honest too, but the page costs one indexed paged read.
 */
export function useUntiApplications(query: UntiApplicationListQuery) {
  return useQuery({
    queryKey: untiApplicationsQueryKey(query),
    queryFn: ({ signal }) => fetchUntiApplications(query, signal),
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    retry: 0,
  });
}

/**
 * #36 as a counter — the register's `useApplicationsCount` trick: `pageSize=1`
 * read for its `meta.total`, the cheapest aggregate #36 offers (no new
 * endpoint, contract unchanged). Its OWN key family, so a one-row answer can
 * never be served to the table.
 */
export function untiApplicationsCountQueryKey(query: UntiApplicationListQuery): readonly ['unti', 'count', UntiApplicationListQuery] {
  return ['unti', 'count', query] as const;
}

export function useUntiApplicationsCount(query: UntiApplicationListQuery) {
  return useQuery({
    queryKey: untiApplicationsCountQueryKey(query),
    queryFn: ({ signal }) => fetchUntiApplications({ ...query, page: 1, pageSize: 1 }, signal),
    select: (envelope) => envelope.meta?.total,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    retry: 0,
  });
}

/**
 * ADDITIVE (v1.9.88, T.Z238) — #36.1 `GET /api/unti/applications/transfert-values`:
 * the DISTINCT «Трансферт» words the RAW mirror rows carry. THE source of the
 * page's select options — the mirror's vocabulary is data, not code, and the
 * page is server-paginated, so the loaded slice can never speak for the whole
 * mirror (the register's #3.1 reading, pointed at the mirror column; its answer
 * is a strict superset of the register's winner-fold facet, which legally omits
 * the non-winner rows this page must offer). The register's quiet-refusal
 * profile: `retry: 0`, a long staleTime, a failing or 403'd read degrades to
 * the `none` option alone, the page otherwise untouched. Non-string junk the
 * wire could carry is dropped, never rendered.
 */
export function useUntiTransfertValues() {
  return useQuery({
    queryKey: ['unti', 'transfert-values'] as const,
    queryFn: async ({ signal }) => {
      const answer = await requestEnvelope<{ values?: unknown }>('GET', UNTI_TRANSFERT_VALUES_PATH, ['data'], { signal });
      const values = Array.isArray(answer.data?.values) ? answer.data.values : [];
      return values.filter((value): value is string => typeof value === 'string');
    },
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: 0,
  });
}

/** Cache identity of one detail read. */
export function untiApplicationDetailQueryKey(id: number): readonly [string, string, number] {
  return ['unti', 'detail', id];
}

/** #37 — one mirror row plus its change history. */
export function useUntiApplicationDetail(id: number | null) {
  return useQuery({
    queryKey: untiApplicationDetailQueryKey(id ?? 0),
    queryFn: ({ signal }) => fetchUntiApplicationDetail(id as number, signal),
    enabled: id !== null,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
    retry: 0,
  });
}
